/**
 * Módulo de plantillas de mantenimiento — CRUD con lista y gestión de actividades ordenadas
 */

import { createCrudMethods } from '../base/crudMethods';

/**
 * Crea el módulo de plantillas de mantenimiento
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.authenticatedRequest - Función de request autenticada
 * @returns {Object} Métodos del módulo plantillas de mantenimiento
 */
export function createPlantillasMantenimientoModule({ authenticatedRequest }) {
  return {
    ...createCrudMethods(
      {
        endpoint: '/api/plantillas-mantenimiento',
        entityName: 'plantillaMantenimiento',
        entityNamePlural: 'plantillasMantenimiento',
        methods: { lista: true }
      },
      { authenticatedRequest }
    ),

    /**
     * Obtiene actividades de una plantilla ordenadas
     *
     * @param {number} plantillaId - ID de la plantilla
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getActividadesPlantilla(plantillaId) {
      return await authenticatedRequest(`/api/plantillas-mantenimiento/${plantillaId}/actividades`);
    },

    /**
     * Crea una actividad en una plantilla
     *
     * @param {number} plantillaId - ID de la plantilla
     * @param {Object} data - { nombre, descripcion }
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async createActividadPlantilla(plantillaId, data) {
      return await authenticatedRequest(`/api/plantillas-mantenimiento/${plantillaId}/actividades`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Actualiza una actividad de plantilla
     *
     * @param {number} plantillaId - ID de la plantilla
     * @param {number} actividadId - ID de la actividad
     * @param {Object} data - Campos a actualizar
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async updateActividadPlantilla(plantillaId, actividadId, data) {
      return await authenticatedRequest(`/api/plantillas-mantenimiento/${plantillaId}/actividades/${actividadId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },

    /**
     * Elimina una actividad de plantilla (soft delete)
     *
     * @param {number} plantillaId - ID de la plantilla
     * @param {number} actividadId - ID de la actividad
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async deleteActividadPlantilla(plantillaId, actividadId) {
      return await authenticatedRequest(`/api/plantillas-mantenimiento/${plantillaId}/actividades/${actividadId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Reordena actividades de una plantilla
     *
     * @param {number} plantillaId - ID de la plantilla
     * @param {Array<{id: number, orden: number}>} ordenArray - Nuevo orden
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async reordenarActividadesPlantilla(plantillaId, ordenArray) {
      return await authenticatedRequest(`/api/plantillas-mantenimiento/${plantillaId}/actividades/reordenar`, {
        method: 'PUT',
        body: JSON.stringify(ordenArray)
      });
    }
  };
}
